import {
  Users, Trophy, Calendar, PiggyBank, Palette, Image, LayoutGrid, Star,
  Shield, Zap, Heart, Settings, Bell, MessageSquare, BarChart3, Clock,
  Globe, Lock, Play, HelpCircle, Smartphone, Crown, Check
} from 'lucide-react';

export const DEFAULT_LOGO_URL = "https://customer-assets.emergentagent.com/job_864cc152-5d80-4928-acac-3bcce367b776/artifacts/dhju5nz8_Grassroots%20Coach%20Logo%20260329.png";

export const BRAND_GREEN = "#007a33";
export const BRAND_GREEN_LIGHT = "#00994d";
export const BRAND_GREEN_DARK = "#005c26";

export const THEMES = {
  dark: {
    name: 'Dark',
    bg: 'bg-gray-900',
    bgSecondary: 'bg-gray-800/50',
    text: 'text-white',
    textSecondary: 'text-gray-400',
    textMuted: 'text-gray-500',
    border: 'border-gray-800',
    borderSecondary: 'border-gray-700',
    card: 'bg-gray-800 border-gray-700',
    cardHover: 'hover:border-[#007a33]/50',
    nav: 'bg-gray-900/95',
    navLink: 'text-gray-300 hover:text-white',
    mobileMenu: 'bg-gray-800 border-gray-700',
    buttonOutline: 'border-gray-600 text-gray-300 hover:bg-gray-800',
    buttonSecondary: 'bg-gray-700 text-white hover:bg-gray-600 hover:!text-white border-gray-600',
    badgeBg: 'bg-[#007a33]/20',
    badgeText: 'text-[#00994d]',
    badgeBorder: 'border-[#007a33]/30',
    accentText: 'text-[#00994d]',
    accentBg: 'bg-[#007a33]/20',
    buttonPrimary: 'bg-[#007a33] hover:bg-[#005c26]',
    windowBar: 'bg-gray-800',
    windowBorder: 'border-gray-700',
    gradient: `radial-gradient(ellipse at 50% 0%, ${BRAND_GREEN}40 0%, transparent 70%)`,
    faqBg: 'bg-gray-800',
    faqBorder: 'border-gray-700',
    faqOpenBorder: 'data-[state=open]:border-[#007a33]/50',
  },
  black: {
    // Pure black variant of dark — replaces navy shades (#111827 etc.) with
    // monochrome blacks (#111111 → #262626). All non-surface styling
    // (text colours, brand greens, badges) mirrors `dark` exactly.
    name: 'Black',
    bg: 'bg-[#111111]',
    bgSecondary: 'bg-[#1a1a1a]/50',
    text: 'text-white',
    textSecondary: 'text-gray-400',
    textMuted: 'text-gray-500',
    border: 'border-[#1a1a1a]',
    borderSecondary: 'border-[#262626]',
    card: 'bg-[#1a1a1a] border-[#262626]',
    cardHover: 'hover:border-[#007a33]/50',
    nav: 'bg-[#111111]/95',
    navLink: 'text-gray-300 hover:text-white',
    mobileMenu: 'bg-[#1a1a1a] border-[#262626]',
    buttonOutline: 'border-[#404040] text-gray-300 hover:bg-[#1a1a1a]',
    buttonSecondary: 'bg-[#262626] text-white hover:bg-[#333333] hover:!text-white border-[#404040]',
    badgeBg: 'bg-[#007a33]/20',
    badgeText: 'text-[#00994d]',
    badgeBorder: 'border-[#007a33]/30',
    accentText: 'text-[#00994d]',
    accentBg: 'bg-[#007a33]/20',
    buttonPrimary: 'bg-[#007a33] hover:bg-[#005c26]',
    windowBar: 'bg-[#1a1a1a]',
    windowBorder: 'border-[#262626]',
    gradient: `radial-gradient(ellipse at 50% 0%, ${BRAND_GREEN}40 0%, transparent 70%)`,
    faqBg: 'bg-[#1a1a1a]',
    faqBorder: 'border-[#262626]',
    faqOpenBorder: 'data-[state=open]:border-[#007a33]/50',
  },
  light: {
    name: 'Brand',
    bg: 'bg-white',
    bgSecondary: 'bg-gray-50',
    text: 'text-gray-900',
    textSecondary: 'text-gray-600',
    textMuted: 'text-gray-500',
    border: 'border-gray-200',
    borderSecondary: 'border-gray-200',
    card: 'bg-white border-gray-200 shadow-sm',
    cardHover: 'hover:border-[#007a33] hover:shadow-md',
    nav: 'bg-[#007a33]',
    navText: 'text-white',
    navLink: 'text-white/80 hover:text-white',
    mobileMenu: 'bg-[#005c26] border-[#004d20]',
    buttonOutline: 'border-[#007a33] text-[#007a33] hover:bg-[#007a33]/10',
    buttonSecondary: 'bg-white text-[#007a33] hover:bg-gray-100 border-white',
    badgeBg: 'bg-[#007a33]/10',
    badgeText: 'text-[#007a33]',
    badgeBorder: 'border-[#007a33]/20',
    accentText: 'text-[#007a33]',
    accentBg: 'bg-[#007a33]/10',
    buttonPrimary: 'bg-[#007a33] hover:bg-[#005c26]',
    windowBar: 'bg-[#007a33]',
    windowBorder: 'border-[#007a33]',
    gradient: `radial-gradient(ellipse at 50% 0%, ${BRAND_GREEN}15 0%, transparent 70%)`,
    faqBg: 'bg-gray-50',
    faqBorder: 'border-gray-200',
    faqOpenBorder: 'data-[state=open]:border-[#007a33]',
  }
};

// Theme keys + canonical display order for the CMS picker.
export const ALL_THEMES = ['dark', 'black', 'light'];

// Maps a raw theme name to its "effective" group (dark/light) so that
// downstream binary `theme === 'dark'` checks in children still work
// without needing to be refactored everywhere.
export const effectiveTheme = (raw) => (raw === 'black' ? 'dark' : raw);

const ICON_MAP = {
  'palette': Palette, 'image': Image, 'layout': LayoutGrid, 'users': Users,
  'trophy': Trophy, 'calendar': Calendar, 'piggybank': PiggyBank, 'star': Star,
  'shield': Shield, 'zap': Zap, 'heart': Heart, 'settings': Settings,
  'bell': Bell, 'message': MessageSquare, 'chart': BarChart3, 'clock': Clock,
  'globe': Globe, 'lock': Lock, 'play': Play, 'help': HelpCircle,
  'phone': Smartphone, 'crown': Crown, 'check': Check,
};

const FEATURE_ICONS = [Users, Trophy, Calendar, PiggyBank];

export const getFeatureIcon = (index) => {
  const Icon = FEATURE_ICONS[index % FEATURE_ICONS.length];
  return <Icon className="w-6 h-6" />;
};

export const getCustomIcon = (iconName) => {
  const Icon = ICON_MAP[iconName] || Users;
  return <Icon className="w-6 h-6" />;
};
